import type { ReactNode } from "react";
import { Button } from "../Button";
import { Modal } from "./Modal";

/**
 * The three rungs of the destructive ladder, in the order the interaction
 * contract's three-question test produces them:
 *
 *  - `"primary"`   — the act is safe and recommended. Accent tile, no glyph on
 *                    the confirm button.
 *  - `"caution"`   — the act is reversible by the same person, but its effect
 *                    reaches past this screen before they could undo it (Q2
 *                    yes): another till, the guest's menu, a printer. Neutral
 *                    tile, NO trash, confirm reads `primary`. The reversal
 *                    belongs in the body copy — the tone does not say it.
 *  - `"danger"`    — the act moves money or marks a document someone signs
 *                    (Q3 yes). Red tile, and the trash glyph is earned.
 *
 * `"danger"` remains the DEFAULT, deliberately: a call site that declares no
 * tone renders exactly as it did before this rung existed, so adding `caution`
 * re-tones nothing on its own. Every re-tone is a visible edit at a call site.
 */
export type ConfirmModalTone = "danger" | "primary" | "caution";

/** Optional identity chip (icon + label + value) shown under the description. */
export type ConfirmModalEntity = {
  icon: ReactNode;
  /** Small uppercase caption, e.g. "Coupon". */
  label: string;
  /** The identifier, rendered mono/bold, e.g. the code or name. */
  value: ReactNode;
};

export type ConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /**
   * Raise this prompt above an already-open modal (e.g. an unsaved-changes
   * guard on a form that lives in a sheet). Forwarded to `Modal`, which tints
   * a lighter backdrop so two layers do not compound to near-black.
   */
  elevated?: boolean;
  /** Header title, e.g. "Delete coupon" / "Publish store". */
  title: string;
  /** Header subtitle, e.g. "This action can't be undone." */
  subtitle?: string;
  /** Body paragraph explaining the consequence. */
  description?: ReactNode;
  /** Confirm button label, e.g. "Delete coupon" / "Publish". */
  confirmLabel: string;
  cancelLabel: string;
  /** Shown on the confirm button while `isSubmitting`. */
  loadingLabel?: string;
  isSubmitting?: boolean;
  /**
   * Holds the confirm button disabled while the body explains why the act
   * cannot go ahead yet. Cancel stays live, so the prompt is never a trap.
   */
  confirmDisabled?: boolean;
  closeLabel?: string;
  /**
   * Visual + semantic tone. "danger" (default) = destructive red tile + button;
   * "caution" = reversible but far-reaching — neutral tile, no trash, primary
   * button; "primary" = safe accent tile + primary button. See
   * `ConfirmModalTone` for the rule that picks between them.
   */
  tone?: ConfirmModalTone;
  /** Override the header tile glyph (defaults: danger = trash, caution = alert, primary = check). */
  icon?: ReactNode;
  /**
   * Override the confirm button's leading icon. Defaults to a trash glyph for
   * danger and none for caution or primary. Pass `null` to force no icon.
   */
  confirmIcon?: ReactNode | null;
  /** Optional identity chip for the subject being acted on. */
  entity?: ConfirmModalEntity;
  /** Extra custom body content rendered below the description + chip. */
  children?: ReactNode;
  /**
   * Names this prompt for tests and QA scripts. The panel gets it, the two
   * footer buttons get `-confirm` / `-cancel` — the same shape `ConfirmDialog`
   * has always had.
   *
   * It matters more here than on an ordinary control: this is the primitive
   * behind most of the platform's destructive confirmations, and without a name
   * none of them could be addressed by one. A browser round driving "86 this
   * dish" or "delete this coupon" had to reach for the footer's last button and
   * hope, which passes just as happily when the wrong button moves into last
   * place.
   */
  testId?: string;
};

/**
 * Reusable confirmation modal matching the design's pattern: a toned icon tile
 * + title/subtitle, a body card with the consequence text and an optional
 * identity chip, and Cancel / confirm footer actions. Built on Modal so it
 * inherits the shared chrome, animation, and a11y.
 *
 * Use everywhere a confirm/delete prompt is needed — pass content, not markup:
 *   <ConfirmModal tone="danger" title="Delete coupon" … entity={{…}} />
 *   <ConfirmModal tone="primary" title="Publish store" confirmLabel="Publish" … />
 */
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  elevated = false,
  title,
  subtitle,
  description,
  confirmLabel,
  cancelLabel,
  loadingLabel,
  isSubmitting = false,
  confirmDisabled = false,
  closeLabel = "Close",
  tone = "danger",
  icon,
  confirmIcon,
  entity,
  children,
  testId = "confirm-modal",
}: ConfirmModalProps) {
  const isDanger = tone === "danger";
  const isCaution = tone === "caution";
  /* No trash on a caution prompt: the glyph is half the message, and a bin
     beside "this dish stops selling on every till" claims a permanence the act
     does not have. A check would claim the opposite — that this is the
     recommended thing to do — so caution gets neither. */
  const tileIcon =
    icon ?? (isDanger ? <TrashIcon /> : isCaution ? <AlertIcon /> : <CheckIcon />);
  const buttonIcon =
    confirmIcon === undefined
      ? isDanger
        ? <TrashSmallIcon />
        : undefined
      : (confirmIcon ?? undefined);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      elevated={elevated}
      size="sm"
      icon={tileIcon}
      iconTone={isDanger ? "danger" : isCaution ? "muted" : "accent"}
      title={title}
      subtitle={subtitle}
      closeLabel={closeLabel}
      data-testid={testId}
      footerActions={
        <>
          <Button
            type="button"
            variant="outline"
            size="md"
            className="max-lg:min-h-10"
            onClick={onClose}
            disabled={isSubmitting}
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={isDanger ? "danger" : "primary"}
            size="md"
            className="max-lg:min-h-10"
            leadingIcon={buttonIcon}
            onClick={onConfirm}
            disabled={isSubmitting || confirmDisabled}
            data-testid={`${testId}-confirm`}
          >
            {isSubmitting && loadingLabel ? loadingLabel : confirmLabel}
          </Button>
        </>
      }
    >
      {description ? (
        <p className="text-[13.5px] leading-[1.55] text-[color:var(--text-secondary)]">
          {description}
        </p>
      ) : null}
      {entity ? (
        <div className="flex items-center gap-[11px] rounded-[12px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-[14px] py-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-[color:var(--card-br,var(--border-light))] bg-[color:var(--surface)] text-[color:var(--text-muted)]">
            {entity.icon}
          </span>
          <div className="min-w-0">
            <div className="mb-[3px] text-[10.5px] font-bold uppercase tracking-[0.08em] text-[color:var(--text-disabled)]">
              {entity.label}
            </div>
            <div className="truncate font-mono text-[14px] font-bold tracking-[0.03em] text-[color:var(--text-primary)]">
              {entity.value}
            </div>
          </div>
        </div>
      ) : null}
      {children}
    </Modal>
  );
}

function TrashIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

function TrashSmallIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}

/** Caution's tile glyph — an exclamation in a circle. Not a bin, not a tick. */
function AlertIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5.5" />
      <path d="M12 16.5h.01" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
